import type { ComponentPropsWithRef, ReactNode } from "react";
import { cx } from "../../lib/cx";
import { Button } from "../Button/Button";
import styles from "./ErrorState.module.css";

export interface ErrorStateProps extends Omit<ComponentPropsWithRef<"div">, "title"> {
  title?: ReactNode;
  /** What went wrong, in plain language, and what the user can do. */
  message?: ReactNode;
  /** Renders a Retry button. */
  onRetry?: () => void;
  retryLabel?: string;
  /** A retry is in flight: the button shows a spinner and blocks activation. */
  retrying?: boolean;
  /** Technical details (request id, error code), collapsed by default. */
  details?: ReactNode;
  /** Announce immediately (role="alert"). Use when the error replaces content the user is waiting for. */
  announce?: boolean;
  headingAs?: "h2" | "h3" | "h4" | "p";
  size?: "sm" | "md";
}

function AlertIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      aria-hidden="true"
      focusable="false"
    >
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="1.75" />
      <path d="M12 7.5v5.5" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" />
      <circle cx="12" cy="16.25" r="1.1" fill="currentColor" />
    </svg>
  );
}

/** A failed load with an optional retry and collapsible technical details. */
export function ErrorState({
  title = "Something went wrong",
  message,
  onRetry,
  retryLabel = "Retry",
  retrying = false,
  details,
  announce = false,
  headingAs: Heading = "h3",
  size = "md",
  className,
  ...rest
}: ErrorStateProps) {
  return (
    <div
      {...rest}
      role={announce ? "alert" : rest.role}
      className={cx(styles.root, className)}
      data-size={size}
    >
      <div className={styles.icon}>
        <AlertIcon />
      </div>
      <Heading className={styles.title}>{title}</Heading>
      {message && <p className={styles.message}>{message}</p>}
      {onRetry && (
        <div className={styles.actions}>
          <Button onClick={onRetry} loading={retrying}>
            {retryLabel}
          </Button>
        </div>
      )}
      {details && (
        <details className={styles.details}>
          <summary className={styles.summary}>Technical details</summary>
          <div className={styles.detailsBody}>{details}</div>
        </details>
      )}
    </div>
  );
}
